/* eslint-disable no-console */

/**
 * Service Worker — opt-in via feature flag `feature.offline.service_worker`.
 *
 * Pattern :
 *   - Le SW est généré au build par Vue CLI PWA Plugin (Workbox) avec
 *     les assets app shell précachés.
 *   - À l'exécution, on ne l'enregistre QUE si l'enterprise courante a
 *     la feature activée (config admin par enterprise).
 *   - Un watcher sur featuresMap active/désactive dynamiquement le SW
 *     sans rechargement de page.
 *
 * Pourquoi opt-in : déployer un SW à toute la base d'un coup est risqué
 * (mauvais cache → page blanche pour tous, sans fallback simple). On
 * commence par activer chez les enterprises pilotes uniquement.
 *
 * À appeler depuis main.ts :
 *
 *   import { wireOfflineServiceWorker } from "./registerServiceWorker";
 *   wireOfflineServiceWorker();
 */

import { register, unregister } from "register-service-worker";
import { watch } from "vue";
import { featuresMap, isFeatureEnabledRaw } from "@/state/api/featuresState";

const FEATURE_KEY = "feature.offline.service_worker";

// État local pour ne pas register/unregister en boucle si featuresMap
// se rafraîchit sans que la valeur effective change.
let currentlyRegistered = false;

function doRegister(): void {
	if (currentlyRegistered) return;
	const swPath = `${process.env.BASE_URL || "/"}service-worker.js`;
	register(swPath, {
		ready() {
			console.log("[SW] App is served from cache by the service worker.");
		},
		registered() {
			console.log("[SW] Service worker registered.");
			currentlyRegistered = true;
		},
		cached() {
			console.log("[SW] Content cached for offline use.");
		},
		updatefound() {
			console.log("[SW] New version downloading.");
		},
		updated() {
			console.log("[SW] New version available; refresh to update.");
		},
		offline() {
			console.log("[SW] No internet — running in offline mode.");
		},
		error(error) {
			console.error("[SW] Registration failed", error);
		},
	});
}

function doUnregister(): void {
	if (!currentlyRegistered) return;
	unregister();
	currentlyRegistered = false;
	console.log("[SW] Service worker unregistered (feature disabled).");
}

/**
 * Branche le watcher sur le feature flag. À appeler une seule fois au
 * boot de l'app (après import du store features).
 *
 * Le SW est désactivé en dev par défaut (HMR webpack incompatible avec
 * le précache SW). On peut forcer l'activation en passant
 * `VUE_APP_FORCE_SW=true` dans le .env.development.
 */
export function wireOfflineServiceWorker(): void {
	const enabledInDev =
		process.env.NODE_ENV !== "production" &&
		process.env.VUE_APP_FORCE_SW !== "true";
	if (enabledInDev) {
		console.log("[SW] Skipped in development (set VUE_APP_FORCE_SW=true to enable).");
		// Purge active : un SW d'un déploiement précédent (prod, staging
		// installé via PWA "Add to home screen", ou ancien dev) peut
		// rester actif dans le navigateur et intercepter les requêtes
		// dev — bloque /api/v1/user/infos, /api/v1/auth/login, etc.
		// (observé en dev : login en pending indéfini, console muette).
		// On unregister TOUS les SW + on vide les caches Workbox au
		// boot dev pour repartir clean. Inoffensif si rien n'est
		// enregistré (les promesses no-op).
		if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
			navigator.serviceWorker.getRegistrations()
				.then((regs) => {
					if (regs.length === 0) return;
					console.log(`[SW] Purging ${regs.length} legacy registration(s) before dev boot…`);
					return Promise.all(
						regs.map((reg) =>
							reg.unregister().then((ok) => {
								if (ok) console.log("[SW] Unregistered", reg.scope);
							}),
						),
					);
				})
				.catch((err) => console.warn("[SW] purge failed", err));
		}
		if (typeof caches !== "undefined") {
			caches.keys()
				.then((names) => {
					if (names.length === 0) return;
					console.log(`[SW] Clearing ${names.length} cache(s):`, names);
					return Promise.all(names.map((n) => caches.delete(n)));
				})
				.catch((err) => console.warn("[SW] cache purge failed", err));
		}
		return;
	}

	// Première évaluation : si features sont déjà chargées (après login
	// rapide), on register tout de suite.
	if (isFeatureEnabledRaw(FEATURE_KEY)) {
		doRegister();
	}

	// Watcher : à chaque changement de la feature (admin l'active /
	// désactive, ou re-fetch des features), on aligne l'état du SW.
	watch(
		() => isFeatureEnabledRaw(FEATURE_KEY),
		(enabled) => {
			if (enabled) doRegister();
			else doUnregister();
		},
	);

	// Et aussi : si tout l'objet featuresMap se réinitialise (logout
	// puis re-login), on remet à plat.
	watch(
		() => featuresMap.value,
		(map) => {
			const enabled = !!map?.[FEATURE_KEY]?.enabled;
			if (enabled) doRegister();
			else doUnregister();
		},
		{ deep: true },
	);
}
